import { useSearchPhotoShelterGalleriesQuery } from 'api/photo_shelter_gallery';
import { useTranslation } from 'react-i18next';
import Form from './Form';
import { useDebounce } from 'use-debounce';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';
import { useState } from 'react';
import { LumenCombobox } from '@photoshelter/socialie-components';

interface Value {
  id: string;
  label: string;
}

interface Props
  extends Omit<React.ComponentProps<typeof Form.Select>, 'onChange' | 'value'> {
  value: Value;
  label: string;
  onChange: (value: Value) => void;
  /**
   * The ID of the Destination to which this gallery belongs.
   */
  destinationId: number;
}

export default function PhotoShelterGallerySelect(props: Props) {
  const { value, onChange, destinationId, label, errors } = props;
  const { t } = useTranslation();

  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, 250);
  const { data, status } = useSearchPhotoShelterGalleriesQuery(
    { destinationId, query: debouncedQuery },
    { skip: !debouncedQuery }
  );

  const emptyText = (() => {
    if (status === QueryStatus.pending)
      return t('photoShelterGallerySelect.loading');
    if (status === QueryStatus.rejected) return t('global.unexpectedError');
    if (debouncedQuery && !data?.length)
      return t('photoShelterGallerySelect.noResults');
    return '';
  })();

  const options = (() => {
    if (!query) return [];
    if (status === QueryStatus.pending) return [];
    if (!data?.length) return [];
    return data.map((gallery) => ({
      value: gallery.id,
      label: gallery.path,
    }));
  })();

  return (
    <Form.Field label={label} errors={errors}>
      <LumenCombobox
        width="100%"
        clearable
        emptyText={emptyText}
        commandProps={{ shouldFilter: false }}
        value={value.id ? { value: value.id, label: value.label } : null}
        placeholder={t('photoShelterGallerySelect.placeholder')}
        options={options}
        popoverProps={{ portal: false }}
        onInputChange={setQuery}
        onValueChange={(value) =>
          onChange({
            id: value?.value || '',
            label: value?.label || '',
          })
        }
      />
    </Form.Field>
  );
}
